import { Suspense, use } from 'react'
import { useLoaderData, useRouteLoaderData } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '~/components/subscription/button'
import { TextLink } from '~/components/subscription/text-link'
import { ZendeskWidget } from '@/components/zendesk-widget'
import { formatDeliverAt, formatTime } from '@/lib/shipment'
import { createClient, JSONAPIResponse } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Addresses } from '@/api-client/Addresses'
import { Cards } from '@/api-client/Cards'
import { Subscription } from '~/api-client/data-contracts'
import { styles } from './styles.css'
import box from './box.svg'

export const loader = async () => {
  const account = await fetchMe()
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0] as unknown as Subscription

  let placements = { data: { data: [] } }
  if (subscription) {
    placements = await fetchPlacements(subscription.id)
  }

  const addresses = await fetchAddresses().catch(() => ({ data: { data: [] } }))

  return [account, subscriptions, placements, addresses]
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions()
}

function fetchPlacements(id) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

function fetchAddresses() {
  return createClient(Addresses).getAddresses()
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => (res as unknown as JSONAPIResponse<typeof fetchDefaultCard>).data.data)
  .catch(() => null)

export const SubscriptionMypageTop: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: subscriptions, included },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: addresses },
    },
  ] = useLoaderData() as [
    JSONAPIResponse<typeof fetchMe>,
    JSONAPIResponse<typeof fetchSubscriptions>,
    JSONAPIResponse<typeof fetchPlacements>,
    JSONAPIResponse<typeof fetchAddresses>
  ]
  const creditCard = use(creditCardPromise)

  const subscription = subscriptions[0]
  const orderableName = included.find((item) => item.type === 'flower_collection_product')?.attributes.name
  const address = addresses.find((addr) => addr.attributes.is_primary)

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>マイページ</PageHeading>
      {subscription && (
        <>
          <p className={styles.note}>{formatTime(1000 * subscription.attributes.skip_deadline)}までスキップ・配送先などの変更が可能です</p>
          <Flex direction="column" align="start" gap="8px" className={styles.card}>
            <h3 className={styles.cardHeading}>次回の到着日</h3>
            <Flex direction="column" align="start" gap="12px">
              {placements[0]?.attributes?.status === 'skipped' ? (
                <>
                  <p className={styles.skipped}>{formatDeliverAt(1000 * subscription.attributes.next_deliver_at)}到着のブーケはスキップしました</p>
                  <Button as="a" href="/subscription/mypage/skip" size="s">
                    スキップを取り消す
                  </Button>
                </>
              ) : (
                <>
                  <Flex direction="column" gap="2px">
                    <p className={styles.date}>{formatDeliverAt(1000 * subscription.attributes.next_deliver_at)}</p>
                    <Flex direction="row" align="center" gap="4px" className={styles.note}>
                      <img src={box} />
                      クロネコヤマトで配送（対面でのお受け取り）
                    </Flex>
                  </Flex>
                  <Button as="a" href="/subscription/mypage/skip" size="s">
                    次回のお届けをスキップ
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </>
      )}
      <Flex direction="column" align="start" gap="8px" className={styles.card}>
        <h3 className={styles.cardHeading}>お届け先</h3>
        <Flex direction="column" align="start" gap="12px">
          {address ? (
            <Flex direction="column" gap="2px" className={styles.text}>
              <span>〒{address.attributes.postal_code}</span>
              <span>
                {address.attributes.state}
                {address.attributes.city_and_street}
              </span>
              <span>{address.attributes.others}</span>
              <span>
                {address.attributes.name}
                {!!address.attributes.sender_name ? `（${address.attributes.sender_name}より）` : ''}
              </span>
            </Flex>
          ) : (
            <p className={styles.warn}>登録されていません</p>
          )}
          <Button as="a" href="/subscription/mypage/address" size="s">
            お届け先を変更する
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" align="start" gap="8px" className={styles.card}>
        <h3 className={styles.cardHeading}>メールアドレス</h3>
        <Flex direction="column" align="start" gap="12px">
          <Flex direction="column" gap="2px" className={styles.text}>
            <span>{account.attributes.email}</span>
          </Flex>
          <Button as="a" href="/subscription/mypage/email" size="s">
            メールアドレスを変更する
          </Button>
          <Button as="a" href="/subscription/mypage/password" size="s">
            パスワードを変更する
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" align="start" gap="8px" className={styles.card}>
        <h3 className={styles.cardHeading}>クレジットカード</h3>
        <Flex direction="column" align="start" gap="12px">
          <Suspense fallback={<></>}>
            {creditCard ? <p className={styles.text}>**** **** **** {creditCard.attributes?.last4}</p> : <p className={styles.warn}>登録されていません</p>}
          </Suspense>
          <Button as="a" href="/subscription/mypage/card" size="s">
            クレジットカードを変更する
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" align="center" gap="24px">
        {subscription && <TextLink to="/subscription/mypage/cancel">定期便の解約</TextLink>}
        <TextLink to="/subscription/mypage/delete">アカウントの削除</TextLink>
      </Flex>
      <ZendeskWidget orderableName={orderableName} userId={account?.id} userEmail={account?.attributes.email} />
    </Flex>
  )
}
